import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import ProfilePicture from '@/components/ProfilePicture';
import { Button } from '@/components/ui/button';
import { UserPlus, Check, Clock, Shield, Compass } from 'lucide-react';

const OrientationLine = ({ label, text }) => (
  <p className="text-sm text-gray-700 leading-snug">
    <span className="text-gray-400">{label}:</span> {text}
  </p>
);

/**
 * Wraps any avatar/trigger; on click shows a member's profile card
 * (avatar, name, Personal Orientation if shared, Section Lead badge, Connect).
 */
export const UserProfilePopover = ({ userId, children, className = '' }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users/${userId}/profile-card`);
      setCard(r.data);
      setStatus(r.data.connection_status);
    } catch (e) {
      toast.error('Could not load profile');
    } finally {
      setLoading(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/users/connections/request`, { user_id: userId });
      setStatus('pending_sent');
      toast.success('Connection request sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally {
      setBusy(false);
    }
  };

  const po = card?.personal_orientation;

  return (
    <Popover onOpenChange={(o) => { if (o && !card) load(); }}>
      <PopoverTrigger
        className={`rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-400 ${className}`}
        data-testid={`profile-trigger-${userId}`}
        aria-label="View profile"
      >
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80" data-testid="profile-card" data-no-translate>
        {loading || !card ? (
          <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <ProfilePicture src={card.profile_picture} alt={card.full_name} size="lg" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">{card.full_name}</p>
                  {card.is_section_lead && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded-full">
                      <Shield className="w-3 h-3" /> Section Lead
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 truncate">@{card.username}</p>
              </div>
            </div>

            {po ? (
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center gap-1.5 text-xs font-medium text-purple-600">
                  <Compass className="w-3.5 h-3.5" /> Personal Orientation
                </div>
                {po.focus && <OrientationLine label="Focus right now" text={po.focus} />}
                {po.experience && <OrientationLine label="Wants to experience" text={po.experience} />}
                {po.show_up && <OrientationLine label="Shows up best" text={po.show_up} />}
                {po.affirmation && (
                  <p className="text-sm italic text-purple-700 pt-1">“{po.affirmation}”</p>
                )}
              </div>
            ) : (
              status !== 'self' && (
                <p className="text-xs text-gray-400 border-t pt-3">
                  This member hasn’t shared a Personal Orientation yet.
                </p>
              )
            )}

            {status !== 'self' && (
              <div className="pt-1">
                {status === 'connected' && (
                  <Button disabled variant="outline" className="w-full" data-testid="profile-connected-btn">
                    <Check className="w-4 h-4 mr-2" /> Connected
                  </Button>
                )}
                {status === 'pending_sent' && (
                  <Button disabled variant="outline" className="w-full" data-testid="profile-pending-btn">
                    <Clock className="w-4 h-4 mr-2" /> Request Sent
                  </Button>
                )}
                {status === 'pending_received' && (
                  <p className="text-xs text-center text-gray-500">
                    This member sent you a request — accept it in Settings → Connections.
                  </p>
                )}
                {status === 'none' && (
                  <Button
                    onClick={connect}
                    disabled={busy}
                    className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
                    data-testid="profile-connect-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" /> {busy ? 'Sending…' : 'Connect'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default UserProfilePopover;
